import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import { ArrowLeft, Check, X, Download, ChevronDown } from "lucide-react";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import logoImage from "@/assets/skylink-logo.png";

interface Student {
  id: string;
  calling_name: string | null;
  office_name: string;
  first_name: string;
  last_name: string;
  batch_id: string | null;
}

interface Instructor {
  id: string;
  name: string;
  email: string;
  user_id: string | null;
  pin: string | null;
}

interface Batch {
  id: string;
  name: string;
}

interface Subject {
  id: string;
  name: string;
}

interface AttendanceRecord {
  studentId: string;
  studentName: string;
  status: "Present" | "Absent";
}

const StudentAttendance = () => {
  const navigate = useNavigate();
  const [userName, setUserName] = useState("");
  const [students, setStudents] = useState<Student[]>([]);
  const [allStudents, setAllStudents] = useState<Student[]>([]);
  const [instructors, setInstructors] = useState<Instructor[]>([]);
  const [batches, setBatches] = useState<Batch[]>([]);
  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedInstructor, setSelectedInstructor] = useState("");
  const [selectedBatches, setSelectedBatches] = useState<string[]>([]);
  const [selectedSubject, setSelectedSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [showAdditional, setShowAdditional] = useState(true);
  const [attendanceRecords, setAttendanceRecords] = useState<AttendanceRecord[]>([]);
  const [summaryRecords, setSummaryRecords] = useState<AttendanceRecord[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [instructorName, setInstructorName] = useState("");
  const [attendanceTime, setAttendanceTime] = useState("");
  const [showPinDialog, setShowPinDialog] = useState(false);
  const [pinInput, setPinInput] = useState("");
  const [isAdmin, setIsAdmin] = useState(false);
  const summaryRef = useRef<HTMLDivElement>(null);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const getLocalDate = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  };

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }

      const { data: profile } = await supabase
        .from("profiles")
        .select("full_name")
        .eq("id", session.user.id)
        .single();

      if (profile) {
        setUserName(profile.full_name);
      }

      // Track current user info
      setCurrentUserId(session.user.id);
      setUserEmail(session.user.email || null);

      // Check if user is admin
      const { data: adminCheck } = await supabase.rpc('has_role', {
        _user_id: session.user.id,
        _role: 'admin'
      });
      setIsAdmin(Boolean(adminCheck));
    };
    checkAuth();
    loadInstructors();
    loadBatches();
    loadSubjects();
    loadAllStudents();
  }, [navigate]);

  useEffect(() => {
    if (selectedBatches.length > 0) {
      loadStudentsByBatches();
    } else {
      setStudents([]);
    }
  }, [selectedBatches]);

  const loadAllStudents = async () => {
    const { data, error } = await supabase
      .from("students")
      .select("id, calling_name, office_name, first_name, last_name, batch_id")
      .order("first_name");

    if (!error && data) {
      setAllStudents(data);
    }
  };

  const loadStudentsByBatches = async () => {
    try {
      const { data, error } = await supabase
        .from("students")
        .select("id, calling_name, office_name, first_name, last_name, batch_id")
        .in("batch_id", selectedBatches)
        .order("office_name");

      if (error) throw error;
      setStudents(data || []);
    } catch (error: any) {
      console.error("Error loading students:", error);
      toast.error("Failed to load students");
    }
  };

  const loadInstructors = async () => {
    const { data, error } = await supabase
      .from("instructors")
      .select("id, name, email, user_id, pin")
      .eq("status", "Active")
      .order("name");

    if (error) {
      toast.error("Failed to load instructors");
      return;
    }

    setInstructors(data || []);
  };

  const loadBatches = async () => {
    const { data, error } = await supabase
      .from("batches")
      .select("id, name")
      .order("name");

    if (error) {
      toast.error("Failed to load batches");
      return;
    }

    setBatches(data || []);
  };

  const loadSubjects = async () => {
    const { data, error } = await supabase
      .from("subjects")
      .select("id, name")
      .order("name");

    if (error) {
      toast.error("Failed to load subjects");
      return;
    }

    setSubjects(data || []);
  };

  const markAttendance = async (studentId: string, studentName: string, status: "Present" | "Absent") => {
    if (!selectedInstructor || selectedBatches.length === 0 || !selectedSubject) {
      toast.error("Please select instructor, batch(es), and subject");
      return;
    }

    setAttendanceRecords(prev => {
      const existing = prev.findIndex(r => r.studentId === studentId);
      if (existing >= 0) {
        const updated = [...prev];
        updated[existing] = { studentId, studentName, status };
        return updated;
      }
      return [...prev, { studentId, studentName, status }];
    });

    toast.success(`${studentName} marked as ${status}`);
  };

  const checkExistingAttendance = async (instructorId: string) => {
    const currentDate = getLocalDate();
    const { data } = await supabase
      .from("instructor_attendance")
      .select("id")
      .eq("instructor_id", instructorId)
      .eq("date", currentDate)
      .maybeSingle();
    
    return !!data;
  };

  const submitAllAttendance = async () => {
    if (!selectedInstructor) {
      toast.error("Please select instructor");
      return;
    }

    if (attendanceRecords.length === 0) {
      toast.error("Please mark attendance for at least one student");
      return;
    }

    setLoading(true);

    try {
      // Get current authenticated user
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast.error("You must be logged in");
        return;
      }

      // Find the selected instructor
      const instructor = instructors.find(i => i.id === selectedInstructor);
      if (!instructor) {
        toast.error("Invalid instructor");
        return;
      }

      // Admin can mark for anyone, instructors can only mark for themselves
      if (!isAdmin && instructor.user_id && instructor.user_id !== user.id) {
        toast.error("You can only mark attendance as yourself");
        return;
      }

      const currentDate = getLocalDate();

      // Check if instructor already marked attendance today
      const hasAttendance = await checkExistingAttendance(selectedInstructor);
      
      if (hasAttendance) {
        // Show PIN dialog for override
        setShowPinDialog(true);
        setLoading(false);
        return;
      }

      // Mark student attendance
      for (const record of attendanceRecords) {
        await supabase.from("student_attendance").upsert({
          student_id: record.studentId,
          status: record.status,
          date: currentDate,
          instructor_id: selectedInstructor,
          subject_id: selectedSubject,
        }, {
          onConflict: 'student_id,date'
        });
      }

      // Auto-mark instructor attendance
      await supabase.from("instructor_attendance").insert({
        instructor_id: selectedInstructor,
        status: "Present",
        date: currentDate,
      });

      setInstructorName(instructor.name);
      setAttendanceTime(new Date().toLocaleString());
      setSummaryRecords([...attendanceRecords]);
      setShowSummary(true);
      toast.success("All attendance marked successfully");
    } catch (error: any) {
      toast.error(error.message || "Failed to mark attendance");
    } finally {
      setLoading(false);
    }
  };

  const handlePinSubmit = async () => {
    const instructor = instructors.find(i => i.id === selectedInstructor);
    
    if (!instructor) {
      toast.error("Invalid instructor");
      return;
    }

    // Check for admin override PIN or instructor's PIN
    const ADMIN_OVERRIDE_PIN = "1319";
    const isAdminOverride = pinInput === ADMIN_OVERRIDE_PIN;
    const isInstructorPin = instructor.pin && pinInput === instructor.pin;

    if (!isAdminOverride && !isInstructorPin) {
      toast.error("Incorrect PIN");
      setPinInput("");
      return;
    }

    setShowPinDialog(false);
    setPinInput("");
    setLoading(true);

    try {
      const currentDate = getLocalDate();

      // Mark student attendance
      for (const record of attendanceRecords) {
        await supabase.from("student_attendance").upsert({
          student_id: record.studentId,
          status: record.status,
          date: currentDate,
          instructor_id: selectedInstructor,
          subject_id: selectedSubject,
        }, {
          onConflict: 'student_id,date'
        });
      }

      setInstructorName(instructor.name);
      setAttendanceTime(new Date().toLocaleString());
      setSummaryRecords([...attendanceRecords]);
      setShowSummary(true);
      toast.success("Attendance marked successfully with PIN override");
    } catch (error: any) {
      toast.error(error.message || "Failed to mark attendance");
    } finally {
      setLoading(false);
    }
  };

  const downloadSummary = async () => {
    if (!summaryRef.current) return;

    try {
      const canvas = await html2canvas(summaryRef.current, {
        scale: 2,
        backgroundColor: "#ffffff",
      });

      // Convert canvas to blob
      canvas.toBlob((blob) => {
        if (!blob) return;
        
        // Create download link
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        const date = new Date().toISOString().split('T')[0];
        link.download = `attendance_${date}.png`;
        link.href = url;
        link.click();
        
        // Cleanup
        URL.revokeObjectURL(url);
      });

      toast.success("Attendance report downloaded!");
    } catch (error) {
      console.error("Error generating image:", error);
      toast.error("Failed to download attendance report");
    }
  };

  const otherStudents = allStudents.filter(s => !selectedBatches.includes(s.batch_id || ''));

  return (
    <Layout userName={userName}>
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate("/dashboard")}
          className="mb-4"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Dashboard
        </Button>

        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-xl sm:text-2xl">Instructor & Batch Selection</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="instructor">Select Yourself *</Label>
                <Select value={selectedInstructor} onValueChange={setSelectedInstructor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select yourself" />
                  </SelectTrigger>
                  <SelectContent>
                    {(isAdmin 
                      ? instructors 
                      : instructors.filter((i) => i.user_id === currentUserId || i.email?.toLowerCase() === (userEmail || '').toLowerCase())
                    ).map((instructor) => (
                      <SelectItem key={instructor.id} value={instructor.id}>
                        {instructor.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-sm text-muted-foreground">Select yourself to mark attendance</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Select Batch(es) *</Label>
                <div className="space-y-2 max-h-48 overflow-y-auto border rounded-md p-3">
                  {batches.map((batch) => (
                    <label key={batch.id} className="flex items-center space-x-2 cursor-pointer hover:bg-accent p-2 rounded">
                      <input
                        type="checkbox"
                        checked={selectedBatches.includes(batch.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSelectedBatches([...selectedBatches, batch.id]);
                          } else {
                            setSelectedBatches(selectedBatches.filter(id => id !== batch.id));
                          }
                        }}
                        className="w-4 h-4 rounded border-input"
                      />
                      <span className="text-sm">{batch.name}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="subject">Select Subject *</Label>
                <Select value={selectedSubject} onValueChange={setSelectedSubject}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select subject" />
                  </SelectTrigger>
                  <SelectContent>
                    {subjects.map((subject) => (
                      <SelectItem key={subject.id} value={subject.id}>
                        {subject.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {selectedBatches.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="text-xl sm:text-2xl">Mark Student Attendance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {students.map((student) => {
                  const record = attendanceRecords.find(r => r.studentId === student.id);
                  return (
                    <div
                      key={student.id}
                      className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-lg gap-3 ${
                        record?.status === "Present" ? "bg-green-50 border-green-200" : 
                        record?.status === "Absent" ? "bg-red-50 border-red-200" : ""
                      }`}
                    >
                      <span className="font-medium text-sm sm:text-base">
                        {student.calling_name || student.office_name}
                      </span>
                      <div className="flex gap-2 w-full sm:w-auto">
                        <Button
                          size="sm"
                          onClick={() => markAttendance(student.id, student.calling_name || student.office_name, "Present")}
                          disabled={loading}
                          className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                        >
                          <Check className="h-4 w-4 mr-1" />
                          Present
                        </Button>
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => markAttendance(student.id, student.calling_name || student.office_name, "Absent")}
                          disabled={loading}
                          className="flex-1 sm:flex-none"
                        >
                          <X className="h-4 w-4 mr-1" />
                          Absent
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {otherStudents.length > 0 && (
                <div className="mt-6">
                  <Button
                    variant="outline"
                    onClick={() => setShowAdditional(!showAdditional)}
                    className="w-full"
                  >
                    <ChevronDown className={`h-4 w-4 mr-2 transition-transform ${showAdditional ? "rotate-180" : ""}`} />
                    {showAdditional ? "Hide" : "Show"} Students from Other Batches
                  </Button>

                  {showAdditional && (
                    <div className="mt-4 space-y-3">
                      {otherStudents.map((student) => {
                        const displayName = student.calling_name || student.office_name || `${student.first_name} ${student.last_name}`;
                        const record = attendanceRecords.find(r => r.studentId === student.id);
                        return (
                          <div
                            key={student.id}
                            className={`flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 border rounded-lg gap-3 ${
                              record?.status === "Present" ? "bg-green-50 border-green-200" : 
                              record?.status === "Absent" ? "bg-red-50 border-red-200" : ""
                            }`}
                          >
                            <span className="font-medium text-sm sm:text-base">
                              {displayName}
                            </span>
                            <div className="flex gap-2 w-full sm:w-auto">
                              <Button
                                size="sm"
                                onClick={() => markAttendance(student.id, displayName, "Present")}
                                disabled={loading}
                                className="bg-green-600 hover:bg-green-700 flex-1 sm:flex-none"
                              >
                                <Check className="h-4 w-4 mr-1" />
                                Present
                              </Button>
                              <Button
                                size="sm"
                                variant="destructive"
                                onClick={() => markAttendance(student.id, displayName, "Absent")}
                                disabled={loading}
                                className="flex-1 sm:flex-none"
                              >
                                <X className="h-4 w-4 mr-1" />
                                Absent
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {attendanceRecords.length > 0 && (
                <Button
                  onClick={submitAllAttendance}
                  disabled={loading}
                  className="w-full mt-6"
                  size="lg"
                >
                  {loading ? "Submitting..." : `Submit Attendance (${attendanceRecords.length} students)`}
                </Button>
              )}
            </CardContent>
          </Card>
        )}

        <Dialog open={showPinDialog} onOpenChange={setShowPinDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Enter PIN to Override</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                This instructor has already marked attendance today. Enter the PIN to mark attendance for another subject.
              </p>
              <div className="space-y-2">
                <Label htmlFor="pin">PIN</Label>
                <Input
                  id="pin"
                  type="password"
                  placeholder="Enter PIN"
                  value={pinInput}
                  onChange={(e) => setPinInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handlePinSubmit()}
                  maxLength={6}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setShowPinDialog(false);
                setPinInput("");
              }}>
                Cancel
              </Button>
              <Button onClick={handlePinSubmit}>
                Verify & Submit
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <Dialog open={showSummary} onOpenChange={(open) => {
          setShowSummary(open);
          if (!open) {
            setAttendanceRecords([]);
            setSummaryRecords([]);
          }
        }}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Attendance Summary</DialogTitle>
            </DialogHeader>
            <div ref={summaryRef} className="p-4 sm:p-8" style={{ backgroundColor: 'hsl(var(--background))', minWidth: '320px' }}>
              <div className="flex flex-col sm:flex-row items-center justify-between mb-6 sm:mb-8 pb-4 gap-4" style={{ borderBottom: '4px solid hsl(var(--primary))' }}>
                <img src={logoImage} alt="Skylink Aviation" className="h-12 sm:h-16" />
                <div className="text-center sm:text-right">
                  <h2 className="text-xl sm:text-3xl font-bold" style={{ color: 'hsl(var(--foreground))' }}>Skylink Aviation</h2>
                  <h3 className="text-base sm:text-xl font-semibold" style={{ color: 'hsl(var(--primary))' }}>Attendance Report</h3>
                </div>
              </div>
              
              <div className="space-y-4 sm:space-y-6 mb-6 sm:mb-8">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6 p-4 sm:p-6 rounded-lg" style={{ backgroundColor: 'hsl(var(--muted))' }}>
                  <div>
                    <p className="text-xs sm:text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Instructor</p>
                    <p className="font-bold text-sm sm:text-lg break-words" style={{ color: 'hsl(var(--foreground))' }}>{instructorName}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Subject</p>
                    <p className="font-bold text-sm sm:text-lg break-words" style={{ color: 'hsl(var(--foreground))' }}>{subjects.find(s => s.id === selectedSubject)?.name}</p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Batch(es)</p>
                    <p className="font-bold text-sm sm:text-lg break-words" style={{ color: 'hsl(var(--foreground))' }}>
                      {selectedBatches.map(id => batches.find(b => b.id === id)?.name).join(", ")}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs sm:text-sm font-medium" style={{ color: 'hsl(var(--muted-foreground))' }}>Date & Time</p>
                    <p className="font-bold text-sm sm:text-lg break-words" style={{ color: 'hsl(var(--foreground))' }}>{attendanceTime}</p>
                  </div>
                </div>
              </div>

              <div className="space-y-4 sm:space-y-6">
                <div className="p-4 sm:p-6 rounded-lg" style={{ backgroundColor: 'hsl(142 76% 96%)', borderLeft: '4px solid hsl(142 71% 45%)' }}>
                  <h4 className="font-bold text-base sm:text-xl mb-3 sm:mb-4" style={{ color: 'hsl(142 71% 45%)' }}>
                    ✓ Present ({summaryRecords.filter(r => r.status === "Present").length})
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    {summaryRecords
                      .filter(r => r.status === "Present")
                      .map((record, idx) => (
                        <p key={idx} className="text-xs sm:text-sm pl-4 break-words" style={{ color: 'hsl(var(--foreground))' }}>• {record.studentName}</p>
                      ))}
                  </div>
                </div>

                <div className="p-4 sm:p-6 rounded-lg" style={{ backgroundColor: 'hsl(0 84% 97%)', borderLeft: '4px solid hsl(var(--destructive))' }}>
                  <h4 className="font-bold text-base sm:text-xl mb-3 sm:mb-4" style={{ color: 'hsl(var(--destructive))' }}>
                    ✗ Absent ({summaryRecords.filter(r => r.status === "Absent").length})
                  </h4>
                  <div className="space-y-1 sm:space-y-2">
                    {summaryRecords
                      .filter(r => r.status === "Absent")
                      .map((record, idx) => (
                        <p key={idx} className="text-xs sm:text-sm pl-4 break-words" style={{ color: 'hsl(var(--foreground))' }}>• {record.studentName}</p>
                      ))}
                  </div>
                </div>
              </div>
              
              <div className="mt-6 sm:mt-8 pt-4 sm:pt-6 text-center" style={{ borderTop: '2px solid hsl(var(--border))' }}>
                <p className="text-xs sm:text-sm break-words" style={{ color: 'hsl(var(--muted-foreground))' }}>Generated on {new Date().toLocaleString()}</p>
                <p className="text-[10px] sm:text-xs mt-1" style={{ color: 'hsl(var(--muted-foreground))' }}>Skylink Ground School Portal</p>
              </div>
            </div>

            <Button onClick={downloadSummary} className="w-full">
              <Download className="h-4 w-4 mr-2" />
              Download as Image
            </Button>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default StudentAttendance;